import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { deliveryFeeForMiles } from '../utils/commission';

// Utility functions for calculations
// WGS84 geodesic (Vincenty inverse) — matches iOS CLLocation.distance exactly.
// The previous spherical haversine (R=3959) overestimates ~0.1–0.5% at
// SKN latitudes, which flips the $3/mile ceil boundary (e.g. $24 vs $25).
const calculateDistance = (coord1, coord2) => {
  if (!coord1 || !coord2) return 0;

  const A = 6378137.0;            // WGS84 semi-major axis (m)
  const F = 1 / 298.257223563;    // WGS84 flattening
  const B = A * (1 - F);          // semi-minor axis

  const toRad = (d) => d * Math.PI / 180;
  const L = toRad(coord2.longitude - coord1.longitude);
  const U1 = Math.atan((1 - F) * Math.tan(toRad(coord1.latitude)));
  const U2 = Math.atan((1 - F) * Math.tan(toRad(coord2.latitude)));
  const sinU1 = Math.sin(U1), cosU1 = Math.cos(U1);
  const sinU2 = Math.sin(U2), cosU2 = Math.cos(U2);

  let lambda = L, prevLambda, iter = 0;
  let sinSigma, cosSigma, sigma, sinAlpha, cosSqAlpha, cos2SigmaM;
  do {
    const sinL = Math.sin(lambda), cosL = Math.cos(lambda);
    sinSigma = Math.sqrt(
      (cosU2 * sinL) ** 2 +
      (cosU1 * sinU2 - sinU1 * cosU2 * cosL) ** 2
    );
    if (sinSigma === 0) return 0; // coincident points
    cosSigma = sinU1 * sinU2 + cosU1 * cosU2 * cosL;
    sigma = Math.atan2(sinSigma, cosSigma);
    sinAlpha = cosU1 * cosU2 * sinL / sinSigma;
    cosSqAlpha = 1 - sinAlpha * sinAlpha;
    cos2SigmaM = cosSqAlpha === 0 ? 0 : cosSigma - 2 * sinU1 * sinU2 / cosSqAlpha;
    const C = F / 16 * cosSqAlpha * (4 + F * (4 - 3 * cosSqAlpha));
    prevLambda = lambda;
    lambda = L + (1 - C) * F * sinAlpha * (
      sigma + C * sinSigma * (cos2SigmaM + C * cosSigma * (-1 + 2 * cos2SigmaM ** 2))
    );
  } while (Math.abs(lambda - prevLambda) > 1e-12 && ++iter < 200);

  if (iter >= 200) {
    // Vincenty failed to converge (near-antipodal) — spherical fallback,
    // same constant family as before (never hit inside SKN).
    const R = 3959;
    const dLat = toRad(coord2.latitude - coord1.latitude);
    const dLon = toRad(coord2.longitude - coord1.longitude);
    const h = Math.sin(dLat/2) ** 2 +
      Math.cos(toRad(coord1.latitude)) * Math.cos(toRad(coord2.latitude)) *
      Math.sin(dLon/2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1-h));
  }

  const uSq = cosSqAlpha * (A * A - B * B) / (B * B);
  const bigA = 1 + uSq / 16384 * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
  const bigB = uSq / 1024 * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)));
  const deltaSigma = bigB * sinSigma * (
    cos2SigmaM + bigB / 4 * (
      cosSigma * (-1 + 2 * cos2SigmaM ** 2) -
      bigB / 6 * cos2SigmaM * (-3 + 4 * sinSigma ** 2) * (-3 + 4 * cos2SigmaM ** 2)
    )
  );
  const meters = B * bigA * (sigma - deltaSigma);
  return meters / 1609.34; // meters → miles, same constant as iOS
};

const calculateDeliveryFee = (distanceMiles, orderType) => {
  if (orderType !== 'delivery') return 0;
  console.log('[DeliveryFee] inputs', { orderType, distanceMiles });
  return deliveryFeeForMiles(distanceMiles);
};

// Address docs store {coordinates:{latitude,longitude}} while restaurant
// locations may be flat — normalize either shape for the haversine calc.
const coordsOf = (loc) =>
  loc?.coordinates || (loc && (loc.latitude != null || loc.longitude != null) ? loc : null);

// Straight-line miles restaurant → customer — same as iOS
// CLLocation.distance used by DeliveryFeeCalculator.
const computeDistanceMiles = (fromLoc, toAddr) => {
  const a = coordsOf(fromLoc);
  const b = coordsOf(toAddr);
  if (!a?.latitude || !a?.longitude || !b?.latitude || !b?.longitude) return 0;
  return calculateDistance(a, b);
};

const CHEF_SERVICE_FEE = 0.02; // 2% Chef service fee

const CartContext = createContext();

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [restaurantLocation, setRestaurantLocation] = useState(null);
  const [restaurantId, setRestaurantId] = useState(null);
  const [restaurantName, setRestaurantName] = useState(null);
  const [restaurantBannerURL, setRestaurantBannerURL] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState(null);
  const [orderType, setOrderType] = useState('pickup'); // 'pickup' or 'delivery'
  const [deliveryDistance, setDeliveryDistance] = useState(0);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('chef_cart');
    if (savedCart) {
      try {
        const cartData = JSON.parse(savedCart);
        setItems(cartData.items || []);
        setOrderType(cartData.orderType || 'pickup');
        setRestaurantLocation(cartData.restaurantLocation || null);
        setRestaurantId(cartData.restaurantId || null);
        setRestaurantName(cartData.restaurantName || null);
        setRestaurantBannerURL(cartData.restaurantBannerURL || null);
        setDeliveryAddress(cartData.deliveryAddress || null);
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    const cartData = {
      items,
      orderType,
      restaurantLocation,
      restaurantId,
      restaurantName,
      restaurantBannerURL,
      deliveryAddress,
      timestamp: Date.now()
    };
    localStorage.setItem('chef_cart', JSON.stringify(cartData));
  }, [items, orderType, restaurantLocation, restaurantId, restaurantName, restaurantBannerURL, deliveryAddress]);

  // Recalculate delivery distance when addresses change
  useEffect(() => {
    if (restaurantLocation && deliveryAddress && orderType === 'delivery') {
      const restCoords = coordsOf(restaurantLocation);
      const addrCoords = coordsOf(deliveryAddress);
      const distance = calculateDistance(restCoords, addrCoords);
      console.log('[DeliveryFee] distance calc', {
        restaurant: restaurantName || restaurantId,
        restaurantCoords: restCoords,
        deliveryCoords: addrCoords,
        distanceMiles: distance,
      });
      setDeliveryDistance(distance);
    } else {
      setDeliveryDistance(0);
    }
  }, [restaurantLocation, deliveryAddress, orderType]);

  // Self-heal: a cart restored from localStorage (or filled before the
  // restaurant doc was fetched) can carry restaurantId with a null or
  // coordinate-less restaurantLocation — which silently zeroes the delivery
  // distance and flattens the fee to the $15 minimum. One refetch per id.
  const healAttempted = useRef(new Set());
  useEffect(() => {
    if (!restaurantId || healAttempted.current.has(restaurantId)) return;
    const coords = coordsOf(restaurantLocation);
    if (coords?.latitude && coords?.longitude) return;
    healAttempted.current.add(restaurantId);
    let cancelled = false;
    getDoc(doc(getFirestore(), 'restaurants', restaurantId))
      .then(snap => {
        if (!cancelled && snap.exists()) {
          setRestaurant({ id: snap.id, ...snap.data() });
        }
      })
      .catch(e => console.warn('restaurantLocation heal failed:', e));
    return () => { cancelled = true; };
  }, [restaurantId, restaurantLocation]);

  const setRestaurant = (restaurant) => {
    setRestaurantId(restaurant.id);
    setRestaurantName(restaurant.name);
    setRestaurantBannerURL(restaurant.bannerURL || restaurant.menuImageURL || '');
    // Coordinates arrive as a GeoPoint, a nested location, or a plain map.
    const coords = restaurant.coordinates || restaurant.location?.coordinates || restaurant.location;
    // iOS Location shape — orders write this verbatim as restaurantLocation
    setRestaurantLocation({
      name: restaurant.name || '',
      address: restaurant.address || restaurant.location?.address || '',
      coordinates: {
        latitude: coords?.latitude || 0,
        longitude: coords?.longitude || 0
      },
      parish: restaurant.parish || restaurant.location?.parish || '',
      island: restaurant.island || restaurant.location?.island || ''
    });
  };

  const addToCart = (item, quantity = 1, restaurantBannerURLParam = null, variantName = null) => {
    // If cart has items from different restaurant, show confirmation before clearing
    let justCleared = false;
    if (restaurantId && item.restaurantId && restaurantId !== item.restaurantId) {
      const confirmed = window.confirm(
        `Your cart contains items from ${restaurantName}. Adding items from a different restaurant will clear your current cart. Continue?`
      );
      if (!confirmed) {
        return; // User cancelled, don't add the item
      }
      clearCart();
      // clearCart() schedules setRestaurantId(null), but `restaurantId` in
      // this closure still holds the OLD id — without this flag the block
      // below is skipped, the new item's restaurant never gets recorded,
      // and the NEXT add passes the different-restaurant check with a null
      // restaurantId → items from two restaurants end up in one cart.
      justCleared = true;
    }

    // Set restaurant if not set (or just cleared for a different restaurant)
    // — fetch the doc so restaurantLocation gets real coordinates.
    // Delivery fee distance + island filtering depend on it.
    if ((!restaurantId || justCleared) && item.restaurantId) {
      setRestaurantId(item.restaurantId);
      setRestaurantName(item.restaurantType || 'Restaurant');
      getDoc(doc(getFirestore(), 'restaurants', item.restaurantId))
        .then(snap => {
          if (snap.exists()) setRestaurant({ id: snap.id, ...snap.data() });
        })
        .catch(e => console.warn('addToCart restaurant fetch failed:', e));
    }

    // Use the passed restaurantBannerURLParam if available, otherwise use the context one
    const bannerURL = restaurantBannerURLParam || restaurantBannerURL;

    setItems(prevItems => {
      // Same item + same size merges quantities; a different size is a new line
      const sameLine = (i) => i.id === item.id && (i.variantName || null) === variantName;
      const existingItem = prevItems.find(sameLine);
      if (existingItem) {
        return prevItems.map(i =>
          sameLine(i)
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }
      const newItem = { ...item, quantity, specialInstructions: '', variantName, restaurantBannerURL: bannerURL };
      return [...prevItems, newItem];
    });
  };

  const removeFromCart = (itemId, variantName = null) => {
    setItems(prevItems => prevItems.filter(item => !(item.id === itemId && (item.variantName || null) === variantName)));
  };

  const updateQuantity = (itemId, quantity, variantName = null) => {
    if (quantity <= 0) {
      removeFromCart(itemId, variantName);
      return;
    }
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId && (item.variantName || null) === variantName ? { ...item, quantity } : item
      )
    );
  };

  const updateSpecialInstructions = (itemId, instructions, variantName = null) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId && (item.variantName || null) === variantName ? { ...item, specialInstructions: instructions } : item
      )
    );
  };

  const updateItemBannerURL = (itemId, bannerURL) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId ? { ...item, restaurantBannerURL: bannerURL } : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
    setRestaurantId(null);
    setRestaurantName(null);
    setRestaurantBannerURL(null);
    setRestaurantLocation(null);
    setDeliveryDistance(0);
  };

  const getSubtotal = () => {
    return items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getChefServiceFee = () => {
    return getSubtotal() * CHEF_SERVICE_FEE;
  };

  const getDeliveryFee = () => {
    return calculateDeliveryFee(deliveryDistance, orderType);
  };

  const getTotal = () => {
    const subtotal = getSubtotal();
    const chefServiceFee = getChefServiceFee();
    const delivery = getDeliveryFee();
    return subtotal + chefServiceFee + delivery;
  };

  const getItemCount = () => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  const setOrderTypeAndLocation = (type, restaurantLoc, deliveryAddr = null) => {
    setOrderType(type);
    setRestaurantLocation(restaurantLoc);
    setDeliveryAddress(deliveryAddr);
    setDeliveryDistance(type === 'delivery' ? computeDistanceMiles(restaurantLoc, deliveryAddr) : 0);
  };

  const updateDeliveryAddress = (address) => {
    setDeliveryAddress(address);
    if (orderType === 'delivery') {
      setDeliveryDistance(computeDistanceMiles(restaurantLocation, address));
    }
  };

  const updateRestaurantLocation = (location) => {
    setRestaurantLocation(location);
  };

  const value = {
    items,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateSpecialInstructions,
    updateItemBannerURL,
    clearCart,
    getSubtotal,
    getChefServiceFee,
    getDeliveryFee,
    getTotal,
    getItemCount,
    orderType,
    restaurantLocation,
    restaurantId,
    restaurantName,
    deliveryAddress,
    deliveryDistance,
    setOrderTypeAndLocation,
    updateDeliveryAddress,
    updateRestaurantLocation,
    setRestaurant,
    CHEF_SERVICE_FEE
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
